import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { brl, dt } from "@/lib/format";
import {
  Users,
  Trash2,
  Search,
  Loader2,
  ShieldCheck,
  Mail,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  Sparkles,
  BadgeDollarSign,
  ChevronDown,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  approved: {
    label: "Aprovado",
    color: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    icon: CheckCircle2,
  },
  pending: {
    label: "Aguardando aprovação",
    color: "text-amber-300",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    icon: Clock,
  },
  rejected: {
    label: "Recusado",
    color: "text-rose-300",
    bg: "bg-rose-500/10",
    border: "border-rose-500/30",
    icon: XCircle,
  },
};

const PLAN_META = {
  trial: {
    label: "Em teste",
    color: "text-amber-300",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
  },
  active: {
    label: "Ativo",
    color: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
  },
  expired: {
    label: "Expirado",
    color: "text-rose-300",
    bg: "bg-rose-500/10",
    border: "border-rose-500/30",
  },
  pending_trial: {
    label: "Aguardando 1º login",
    color: "text-slate-300",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
  },
  admin: {
    label: "Admin",
    color: "text-blue-300",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
  },
  pending: {
    label: "Sem acesso",
    color: "text-slate-400",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
  },
  rejected: {
    label: "Sem acesso",
    color: "text-slate-400",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
  },
};

const PLAN_LABELS = {
  mensal: "Mensal",
  semestral: "Semestral",
  anual: "Anual",
};

const fmtDate = (iso) => {
  if (!iso) return "—";

  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
};

export default function AdminUsersDialog({ open, onOpenChange }) {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");

  const load = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const setStatus = async (u, status) => {
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status });

      toast.success(
        status === "approved"
          ? "Cadastro aprovado"
          : status === "rejected"
            ? "Cadastro recusado"
            : "Status atualizado",
      );

      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const activatePlan = async (u, planType) => {
    try {
      await api.post(`/admin/users/${u.id}/activate-plan`, {
        plan_type: planType,
      });

      toast.success(`Plano ${planType} ativado`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const expirePlan = async (u) => {
    if (
      !window.confirm(
        `Expirar o plano de "${u.email}"? O acesso será bloqueado.`,
      )
    ) {
      return;
    }

    try {
      await api.post(`/admin/users/${u.id}/expire-plan`);
      toast.success("Plano expirado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });

      toast.success(
        role === "admin"
          ? "Usuário promovido a ADMIN."
          : role === "user"
            ? "Usuário alterado para USUÁRIO."
            : "Função do usuário alterada.",
      );

      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (
      !window.confirm(
        `Excluir a conta "${u.email}"? Isso também apagará ${u.clients_count} cliente(s) desta conta. Ação irreversível.`,
      )
    ) {
      return;
    }

    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Conta excluída");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = users.filter((u) => {
    if (tab === "pending" && u.status !== "pending") return false;
    if (tab === "trial" && u.plan_state !== "trial") return false;
    if (tab === "active" && u.plan_state !== "active") return false;
    if (tab === "expired" && u.plan_state !== "expired") return false;

    if (!q) return true;

    const s = q.toLowerCase();

    return (
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.business_name || "").toLowerCase().includes(s)
    );
  });

  const pendingCount = users.filter(
    (u) => u.status === "pending",
  ).length;

  const trialCount = users.filter(
    (u) => u.plan_state === "trial",
  ).length;

  const activeCount = users.filter(
    (u) => u.plan_state === "active",
  ).length;

  const expiredCount = users.filter(
    (u) => u.plan_state === "expired",
  ).length;

  const TABS = [
    {
      key: "all",
      label: `Todas · ${users.length}`,
      testid: "admin-tab-all",
    },
    {
      key: "pending",
      label: `Aguardando · ${pendingCount}`,
      testid: "admin-tab-pending",
    },
    {
      key: "trial",
      label: `Em teste · ${trialCount}`,
      testid: "admin-tab-trial",
    },
    {
      key: "active",
      label: `Ativos · ${activeCount}`,
      testid: "admin-tab-active",
    },
    {
      key: "expired",
      label: `Expirados · ${expiredCount}`,
      testid: "admin-tab-expired",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-full max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] overflow-x-hidden overflow-y-auto p-3 sm:p-6 bg-[#0B132B] border-slate-800 text-slate-100"
        data-testid="admin-users-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ fontFamily: "Outfit" }}
          >
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            Contas cadastradas no painel
          </DialogTitle>

          <DialogDescription className="text-slate-400">
            Aprove cadastros, gerencie planos e funções. O ADMIN GERAL
            possui controle total, inclusive sobre contas ADMIN.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 my-2 flex-wrap">
          {TABS.map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={tab === t.key ? "default" : "outline"}
              className={
                tab === t.key
                  ? "bg-blue-600 hover:bg-blue-500 text-white"
                  : "border-slate-700 text-slate-300 hover:bg-slate-800"
              }
              onClick={() => setTab(t.key)}
              data-testid={t.testid}
            >
              {t.label}
            </Button>
          ))}
        </div>

        <div className="relative mb-2">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />

          <Input
            placeholder="Buscar por e-mail, nome ou negócio..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 bg-slate-900/60 border-slate-700 focus:border-blue-500"
            data-testid="admin-search"
          />
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando contas...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-14 text-center text-slate-500">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nenhuma conta encontrada.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((u) => {
              const st = STATUS_META[u.status] || STATUS_META.approved;
              const StIcon = st.icon;
              const pl = PLAN_META[u.plan_state] || PLAN_META.pending_trial;

              return (
                <div
                  key={u.id}
                  className="glass-card p-4 flex flex-col gap-3"
                  data-testid={`admin-user-${u.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-100 truncate">
                          {u.business_name || u.name || u.email}
                        </div>

                        {u.role === "super_admin" && (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 border">
                            ADMIN GERAL
                          </Badge>
                        )}

                        {u.role === "admin" && (
                          <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30 border">
                            ADMIN
                          </Badge>
                        )}

                        {u.role === "user" && (
                          <Badge className="bg-slate-500/15 text-slate-300 border-slate-500/30 border">
                            USUÁRIO
                          </Badge>
                        )}

                        <Badge
                          variant="outline"
                          className={`${st.bg} ${st.color} ${st.border} inline-flex items-center gap-1`}
                          data-testid={`admin-status-${u.id}`}
                        >
                          <StIcon className="w-3 h-3" />
                          {st.label}
                        </Badge>

                        {u.role !== "admin" &&
                          u.role !== "super_admin" && (
                            <Badge
                              variant="outline"
                              className={`${pl.bg} ${pl.color} ${pl.border} inline-flex items-center gap-1`}
                              data-testid={`admin-plan-state-${u.id}`}
                            >
                              <Sparkles className="w-3 h-3" />
                              {pl.label}

                              {u.plan_days_remaining != null &&
                                ["trial", "active"].includes(u.plan_state) && (
                                  <span className="opacity-80">
                                    · {u.plan_days_remaining}d
                                  </span>
                                )}
                            </Badge>
                          )}
                      </div>

                      <div className="text-xs text-slate-400 flex items-center gap-3 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {u.email}
                        </span>

                        {u.business_name && u.name && (
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {u.name}
                          </span>
                        )}

                        <span className="text-slate-500">
                          Cadastrado em {dt(u.created_at)}
                        </span>
                      </div>

                      {u.role !== "admin" &&
                        u.role !== "super_admin" && (
                          <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-1 flex-wrap font-mono">
                            <span>
                              Trial início: {fmtDate(u.trial_started_at)}
                            </span>

                            <span>
                              Plano: {u.plan_type || "—"}
                            </span>

                            <span>
                              Expira em: {fmtDate(u.plan_expires_at)}
                            </span>

                            {u.created_by_email && (
                              <span>
                                Criado por: {u.created_by_email}
                              </span>
                            )}
                          </div>
                        )}
                    </div>

                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 text-xs">
                      <div className="text-center min-w-[4.5rem]">
                        <div className="text-[10px] uppercase text-slate-500 tracking-widest">
                          Clientes
                        </div>
                        <div className="text-slate-100 font-mono font-semibold">
                          {u.clients_count}
                        </div>
                      </div>

                      <div className="text-center min-w-[4.5rem]">
                        <div className="text-[10px] uppercase text-slate-500 tracking-widest">
                          Emprestado
                        </div>
                        <div className="text-blue-300 font-mono font-semibold">
                          {brl(u.total_lent)}
                        </div>
                      </div>

                      <div className="text-center min-w-[4.5rem]">
                        <div className="text-[10px] uppercase text-slate-500 tracking-widest">
                          Saldo
                        </div>
                        <div className="text-amber-300 font-mono font-semibold">
                          {brl(u.balance)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {u.id !== user?.id && (
                    <div className="flex items-center gap-2 flex-wrap justify-end border-t border-slate-800/70 pt-3">
                      {u.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                            onClick={() => setStatus(u, "approved")}
                            data-testid={`admin-approve-${u.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Aprovar
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"
                            onClick={() => setStatus(u, "rejected")}
                            data-testid={`admin-reject-${u.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Recusar
                          </Button>
                        </>
                      )}

                      {u.status === "approved" && (
                        <>
                          <DropdownMenu>
                            {u.role !== "admin" &&
                              u.role !== "super_admin" && (
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    className="bg-blue-600 hover:bg-blue-500 text-white h-8"
                                    data-testid={`admin-activate-plan-${u.id}`}
                                  >
                                    <BadgeDollarSign className="w-3.5 h-3.5 mr-1" />
                                    Ativar plano
                                    <ChevronDown className="w-3 h-3 ml-1" />
                                  </Button>
                                </DropdownMenuTrigger>
                              )}

                            <DropdownMenuContent
                              align="end"
                              className="bg-slate-900 border-slate-700 text-slate-200"
                            >
                              {u.role !== "admin" &&
                                u.role !== "super_admin" &&
                                Object.entries(PLAN_LABELS).map(
                                  ([k, label]) => (
                                    <DropdownMenuItem
                                      key={k}
                                      className="cursor-pointer"
                                      onClick={() => activatePlan(u, k)}
                                      data-testid={`admin-activate-${k}-${u.id}`}
                                    >
                                      <Sparkles className="w-4 h-4 mr-2 text-emerald-400" />
                                      {label}
                                    </DropdownMenuItem>
                                  ),
                                )}

                              <DropdownMenuSeparator />

                              {u.id !== user?.id &&
                                ((u.role !== "admin" &&
                                  u.role !== "super_admin") ||
                                  user?.role === "super_admin") && (
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      changeRole(
                                        u,
                                        u.role === "admin" ? "user" : "admin",
                                      )
                                    }
                                  >
                                    {u.role === "admin" ||
                                    u.role === "super_admin"
                                      ? "Rebaixar para Usuário"
                                      : "Promover a ADMIN"}
                                  </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {u.role !== "admin" &&
                            u.role !== "super_admin" &&
                            u.plan_state === "active" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"
                                onClick={() => expirePlan(u)}
                                data-testid={`admin-expire-plan-${u.id}`}
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                Expirar plano
                              </Button>
                            )}

                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-8"
                            onClick={() => setStatus(u, "rejected")}
                            data-testid={`admin-block-${u.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Bloquear
                          </Button>
                        </>
                      )}

                      {u.status !== "approved" &&
                        u.id !== user?.id &&
                        ((u.role !== "admin" &&
                          u.role !== "super_admin") ||
                          user?.role === "super_admin") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-8"
                            onClick={() => setStatus(u, "approved")}
                            data-testid={`admin-reactivate-${u.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Reativar
                          </Button>
                        )}

                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-rose-300 h-8"
                        onClick={() => remove(u)}
                        data-testid={`admin-delete-${u.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
